// Several chosen clips moved together on the timeline (spec group-moves.md). Pure: the reducer's own
// `moveClip`, one a clip, checked part by part before any is made.

import { MAX_TOTAL_MS, editTimeline, refusal, type Timeline, type TimelineEdit } from "./timeline";

/** A group's move: the time it moves by, and its one step, or null where the group stays. */
export interface GroupMove {
  byMs: number;
  edit: TimelineEdit | null;
}

/**
 * The chosen clips moved by one time, each on its own track, as one step. The time is held so the
 * earliest starts at 0 at most and the latest ends within the longest video, and the whole move is
 * refused (null) when the reducer would refuse any part of it (a locked clip, another clip in the
 * way): a group never comes apart, as `many` alone would leave its refused parts behind.
 */
export function groupMove(timeline: Timeline, ids: readonly string[], byMs: number): GroupMove {
  const clips = timeline.clips.filter((clip) => ids.includes(clip.id));
  if (clips.length === 0) return { byMs: 0, edit: null };
  const earliest = Math.min(...clips.map((clip) => clip.startMs));
  const latest = Math.max(...clips.map((clip) => clip.endMs));
  const shift = Math.round(Math.max(-earliest, Math.min(MAX_TOTAL_MS - latest, byMs)));
  if (shift === 0) return { byMs: 0, edit: null };
  // The furthest along the move goes first, so no clip meets one of its group still in its place.
  const ordered = [...clips].sort((one, other) =>
    shift > 0 ? other.startMs - one.startMs : one.startMs - other.startMs,
  );
  const edits: TimelineEdit[] = ordered.map((clip) => ({ type: "moveClip", id: clip.id, startMs: clip.startMs + shift }));
  let next = timeline;
  for (const edit of edits) {
    if (refusal(next, edit) !== null) return { byMs: shift, edit: null };
    next = editTimeline(next, edit);
  }
  return { byMs: shift, edit: { type: "many", edits: [...edits, ...keptTransitions(timeline, next, ids)] } };
}

/**
 * The edits that put back, as they were, the transitions between two clips of the group: their cut
 * moves with them, but each part, made alone, opens it for a moment, and a transition off a cut is
 * dropped (the AO review of #1130). One whose cut is gone, a group clip's with another, stays gone.
 */
function keptTransitions(before: Timeline, after: Timeline, ids: readonly string[]): TimelineEdit[] {
  const kept: TimelineEdit[] = [];
  let next = after;
  for (const transition of before.transitions) {
    if (!ids.includes(transition.fromId) || !ids.includes(transition.toId)) continue;
    if (next.transitions.some((each) => each.id === transition.id)) continue;
    const { id, fromId, toId, kind, durationMs } = transition;
    const back: TimelineEdit[] = [{ type: "setTransition", id, fromId, toId, kind }];
    const put = editTimeline(next, back[0]!).transitions.find((each) => each.id === id);
    if (put === undefined) continue;
    if (put.durationMs !== durationMs) back.push({ type: "setTransitionDuration", id, durationMs });
    for (const edit of back) next = editTimeline(next, edit);
    kept.push(...back);
  }
  return kept;
}

/** A group's move as its landing tag says it: "+1.20 s", "−0.50 s". */
export function shiftText(byMs: number): string {
  return `${byMs < 0 ? "−" : "+"}${(Math.abs(byMs) / 1000).toFixed(2)} s`;
}
